import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Signpost } from "@phosphor-icons/react";
import { useApi } from "@/lib/api";
import { STEPS, TOUR_EVENT, Tour } from "@/components/app/DemoTour";

const PENDING = "cr-welcome-tour-pending";

const finalStep = (owner, go) => (owner
  ? { final: true, title: "Connect your services", body: "Your runway is only as good as your data. Link these on the Integrations page and the dashboard fills in automatically:",
      list: ["Bank account for live balances and spending", "Stripe for revenue and MRR", "QuickBooks for bills and invoices"],
      action: { label: "Connect my services", run: go } }
  : { final: true, title: "You're all set", body: "Your workspace owner manages the connections, so the numbers you see are already live. Explore scenarios, ask the Calibrator and leave notes for the team." });

// First visit to the dashboard after sign-up: the demo tour, ending with a getting-started step.
export const WelcomeTour = ({ me }) => {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const owner = me.role === "owner";
  const steps = useMemo(() => [...STEPS, finalStep(owner, () => navigate("/app/integrations"))], [owner, navigate]);
  const saved = useRef(me.tour_seen);
  const onDone = useCallback(() => {
    if (saved.current) return;
    saved.current = true;
    api.post("/tour/seen").then(() => qc.invalidateQueries({ queryKey: ["me"] })).catch(() => { saved.current = false; });
  }, [api, qc]);
  const [replay] = useState(() => sessionStorage.getItem(PENDING) === "1");
  useEffect(() => { sessionStorage.removeItem(PENDING); }, []);
  return <Tour steps={steps} accent="brand" autoStart={!me.tour_seen || replay} onDone={onDone} />;
};

export const TourButton = ({ slim, onDashboard, testid = "take-tour-btn" }) => {
  const navigate = useNavigate();
  const start = () => {
    if (onDashboard) window.dispatchEvent(new Event(TOUR_EVENT));
    else { sessionStorage.setItem(PENDING, "1"); navigate("/app"); }
  };
  return (
    <button data-testid={testid} onClick={start} title="Take the tour"
      className={`flex items-center rounded-lg text-sm text-[#A3A3A3] hover:text-white hover:bg-[#121212] transition-colors ${slim ? "h-10 w-10 justify-center" : "gap-3 px-3 py-2.5"}`}>
      <Signpost size={18} />{!slim && "Take the tour"}
    </button>
  );
};
